import React, { useCallback, useMemo } from "react"
import { Check } from "@mui/icons-material"
import { FormControlLabel, FormGroup } from "@mui/material"
import MuiCheckBox from "@mui/material/Checkbox"
import { useFormContext, useController } from "react-hook-form"
import { FormField, Option } from "./types"

import styles from "./Forms.module.scss"
import { FormLabel } from "./FormLabel"
import { trackFormEvent } from "./utils"

export interface Props extends FormField {
  options: Option[]
  sortOptions?: boolean
}

const CheckBoxes = ({ className, label, name, rules, options, sortOptions = true, tracking }: Props) => {
  const { control } = useFormContext()
  const controller = useController({ name, control, rules })

  const error = controller.formState.errors[name]
  const errorProps = error ? { error: error.message?.toString() } : {}

  const selectedOptions: Option[] = useMemo(() => controller.field.value || [], [controller])

  const handleClick = useCallback(
    (option: Option) => async () => {
      const isSelectedOption = selectedOptions.find(({ value }) => value === option.value)
      const newValue = isSelectedOption
        ? selectedOptions.filter(({ value }) => value !== option.value)
        : selectedOptions.concat([option])

      controller.field.onChange(newValue)

      if (tracking?.shouldTrackValueChange && newValue.length) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [controller.field, label, name, selectedOptions, tracking]
  )

  return (
    <FormGroup className={className}>
      {label && <FormLabel>{label}</FormLabel>}
      {options
        .sort((a, b) => (sortOptions ? a.label.localeCompare(b.label) : 0))
        .map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <MuiCheckBox
                checkedIcon={<Check />}
                onChange={handleClick(option)}
                checked={!!selectedOptions.find(({ value }) => option.value === value)}
              />
            }
            label={option.label}
          />
        ))}

      {error && <span className={styles.errorLabel}>{errorProps.error}</span>}
    </FormGroup>
  )
}

export default CheckBoxes
